import React, { useEffect, useMemo, useState } from "react";
import {
    Area,
    Bar,
    ComposedChart,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useSelector } from "react-redux";
import { useDemandInventoryLineGraphData, usePotentialOverstockLineGraphData } from "../../services/redux/hooks/useInventory";
import { IDemandPurchase, IInventoryTrend, ILTTrend, InventoryGraphPayload, IPotentialStockOutInventoryTrend } from "../../services/interface/IAlerts";
import moment from "moment";
import { formatNumber, formatter } from "../../utils/formatter";
import { IRoot } from "../../services/interface/IHome";

const CustomLegend = (props: any) => {
    const { payload } = props;
    return (
        <ul className="flex items-center gap-2">
            {payload.map((entry: any, index: number) => (
                <li key={`item-${index}`} className="flex items-center mb-1">
                    <svg width="40" height="10">
                        <line x1="0" y1="5" x2="15" y2="5" stroke={entry.color} strokeWidth="2" />
                        <polygon points="20,1 24,5 20,9 16,5" fill={entry.color} />
                        <line x1="25" y1="5" x2="40" y2="5" stroke={entry.color} strokeWidth="2" />
                    </svg>
                    <span style={{ color: entry.color }} className="ml-2 font-semibold font-[0.6vw]">
                        {entry.value === "onHand" ? "On Hand (Qty.)" :
                            entry.value === "onhand" ? "On Hand (Qty.)" :
                                entry.value === "effectivess" ? "Safety Stock (Qty.)" :
                                    entry.value === "adjusted" ? "Safety Stock (Qty.) + Cycle Stock (Qty.)" : entry.value}
                    </span>
                </li>
            ))}
        </ul>
    );
};

const DemandPurchaseLegend = (props: any) => {
    const { payload } = props;
    return (
        <ul className="flex items-center gap-2">
            {payload?.filter((entry: any) => entry.dataKey !== "PrevForecast").map((entry: any, index: number) => (
                <li key={`item-${index}`} className="flex items-center mb-1">
                    <svg width="30" height="8">
                        <line x1="0" y1="4" x2="10" y2="4" stroke={entry?.color} strokeWidth="1.5" />
                        <polygon points="12,1 16,4 12,7 8,4" fill={entry?.color} />
                        <line x1="15" y1="4" x2="25" y2="4" stroke={entry?.color} strokeWidth="1.5" />
                    </svg>
                    <span style={{ color: entry.color }} className="ml-2 font-semibold font-[0.6vw]">
                        {entry.value === "Forecast" ? "Forecast Next 3M" :
                            entry.value === "Purchase" ? "Purchase Last 3M" :
                                entry.value === "Demand" ? "Demand Last 3M" : ""}
                    </span>
                </li>
            ))}
        </ul>
    )
}

const TargetInventoryGraph = (props: any) => {

    const alertType = useMemo(() => new URLSearchParams(location.search).get("alert"), [location.search]);
    const updatedAlert = String(alertType)?.charAt(0).toLowerCase() + alertType?.slice(1) || "";
    const selectedGBPFilter = useSelector((state: any) => state.filter.selectedGBPFilter);
    const [isActive, setIsActive] = useState<number>(0);
    const [invGraphPayload, setInvGraphPayload] = useState<InventoryGraphPayload>({
        alertType: updatedAlert,
        clustername: [],
        futureSegmentation: [],
        l2: [],
        l3: [],
        material: [],
        materialDescription: [],
        mrpType: [],
        plant: [],
        vendorName: [],
    });
    const [invGraphData, setInvGraphData] = useState<any[]>([]);
    const [invDemandPurchaseData, setInvDemanPurchaseData] = useState<IDemandPurchase | any>();
    const [invTrendGraphData, setInvTrendGraphData] = useState<IInventoryTrend[]>();
    const [invLTTrendGraphData, setInvLTTrendGraphData] = useState<ILTTrend[]>();
    const applyFilter = useSelector((state:IRoot)=>state.filter.applyFilter);
    // for potential stock out
    const { mutate: mutateInvData, data: inventoryLineGraphData } = useDemandInventoryLineGraphData();
    const { mutate: mutateInvGraphData, data: inventoryLineGraphPSOdata } = usePotentialOverstockLineGraphData();

    useEffect(() => {
        if(applyFilter.status || applyFilter.rowNo===-1){
            const payload = {
                ...invGraphPayload,
                ...selectedGBPFilter
            }
            mutateInvGraphData(payload);
            mutateInvData(payload);
        }
    }, [applyFilter]);

    useEffect(()=>{
        if(alertType === "PotentialStockout" && inventoryLineGraphPSOdata){
            let finalData:any[] = [];
            let inventory = inventoryLineGraphPSOdata?.data;
            inventory.forEach((element:IPotentialStockOutInventoryTrend,index:number) => {
                let obj = {
                    monthStartDate: index === inventory.length - 1 ? "End of NM" : 
                                    index === inventory.length - 2 ? "End of CM" :
                                    moment(element?.monthstartdate?.slice(0, 10)).format("MMM'YY"),
                    adjusted: element?.adjusted,
                    effectivess: element?.effectivess,
                    onHand: index === inventory.length - 1 ? element?.InvAtEndOfLT2 : 
                            index === inventory.length - 2 ? element?.InvAtEndOfLT1 : 
                            element?.onhand
                  };
                  finalData.push(obj);
            });
            setInvGraphData(finalData);
        }
    },[inventoryLineGraphPSOdata]);

    useEffect(() => {
        let finalData: any = [];
        if (inventoryLineGraphData) {
            let demand = inventoryLineGraphData?.demand;
            let inventory = inventoryLineGraphData?.inventory;
            let replt = inventoryLineGraphData?.repData;

            let demandforecast: any = [];
            demand.forEach((element: any, index: number) => {
                let obj = {
                    dayDate: moment(element?.dayDate?.slice(0, 10)).format("MMM'YY"),
                    Demand: index < 3 ? element?.demand : null,
                    Forecast: index >= 3 ? element?.forecast : null,
                    Purchase: index < 3 ? element?.purchases : null,
                    PrevForecast: index === 2 ? element?.demand : (index === 3 ? element?.forecast : null) // Connect Demand and Forecast with a dotted line
                };
                demandforecast.push(obj);
            });
            setInvDemanPurchaseData(demandforecast);

            let inventoryTrend: IInventoryTrend[] = [];
            inventory.forEach((element: IInventoryTrend, index: number) => {
                let obj = {
                    monthdate: moment(element?.monthdate?.slice(0, 10)).format("MMM'YY"),
                    onhand: element?.onhand
                }
                inventoryTrend.push(obj);
            });
            setInvTrendGraphData(inventoryTrend);

            let ltTrend: ILTTrend[] = [];
            replt?.forEach((element: ILTTrend, index: number) => {
                let obj = {
                    monthdate: moment(element?.monthdate?.slice(0, 10)).format("MMM'YY"),
                    replt: element?.replt
                }
                ltTrend.push(obj);
            });
            setInvLTTrendGraphData(ltTrend);
        }
    }, [inventoryLineGraphData]);

    return (
        <div className="w-[100%] h-[100%] bg-[#FFF] shadow rounded px-[2%] py-[12px] border-[1px] border-[#BEBEBE] flex flex-col">
            <div className="flex justify-between items-center mb-[0.5vw]">
                <span className="text-mainGreen font-semibold chart-title self-start">
                    {props.title}
                </span>
            </div>
            <div className="flex h-[30vh]">
                {props.flag == 1 && 
                    <div className="flex-grow h-full">
                        <ResponsiveContainer width={"100%"} height={"100%"}>
                            <ComposedChart data={invGraphData} margin={{ top: 20, right: 20, bottom: 0, left: -10 }}>
                                <XAxis stroke="#949494" dataKey="monthStartDate" fontSize={"0.8vw"} padding={{ left: 0, right: 30 }} />
                                <YAxis interval="equidistantPreserveStart" tickFormatter={formatter} stroke="#949494" fontSize={"0.8vw"} tickCount={10} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend
                                    content={<CustomLegend />}
                                    align="left"
                                    verticalAlign="top"
                                    iconSize={8}
                                    wrapperStyle={{ left: 0, top: 0, marginBottom: "10px" }}
                                />
                                <Area type="monotone" dataKey="effectivess" stroke="#012169" fillOpacity="0.2"
                                activeDot={{ stroke: '#012169', fill: 'white', strokeWidth: 2, r: 4 }} />
                                <Area type="monotone" dataKey="adjusted" stroke="#62B5E5" fillOpacity="0.2"
                                activeDot={{ stroke: '#62B5E5', fill: 'white', strokeWidth: 2, r: 4 }} />
                                <Line type="monotone" dataKey="onHand" stroke="#86BC25"
                                strokeWidth="2"
                                dot={{ fill: "#86BC25", r: 2 }}
                                activeDot={{stroke:'#86BC25', fill: 'white', strokeWidth:2, r: 4}} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                }
            </div>
        </div>
    );
}

const CustomTooltip = (props: any) => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#fff] border-[1px] border-[#BEBEBE] p-2 rounded rs-bg">
                <p className="text-black text-fs16 font-bold mb-3">
                    {payload[0]?.payload?.monthStartDate ||
                        payload[0]?.payload?.monthdate}
                </p>
                <div className="flex flex-col gap-2">
                    {payload?.map(($: any) => (
                        <div className="flex flex-col" key={$?.name}>
                            <p className="text-black text-fs14">
                                {$.payload?.monthStartDate === "End of NM" && $.name === "onHand" ? "End of NM" :
                                $.payload?.monthStartDate === "End of CM" && $.name === "onHand" ? "End of CM" : 
                                $.name === "onHand" ? "On Hand (Qty.)" :
                                $.name === "onhand" ? "On Hand (Qty.)" :
                                $.name === "effectivess" ? "Safety Stock (Qty.)" :
                                $.name === "adjusted" ? "Safety Stock (Qty.) + Cycle Stock (Qty.)" : $.name}
                            </p>
                            <p style={{ color: $.color }} className="text-black text-fs20 font-bold">
                                {["onHand","onhand","effectivess","adjusted"].includes($.name) ? formatter($.value) : $.value}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    return null;
};

const DemandPurchaseTooltip = (props: any) => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#fff] border-[1px] border-[#BEBEBE] p-2 rounded rs-bg">
                <p className="text-black text-fs16 font-bold mb-3">
                    {payload[0]?.payload?.dayDate}
                </p>
                <div className="flex flex-row gap-2">
                    {payload?.filter(($: any) => $.name !== "PrevForecast").map(($: any) => (
                        <div className="flex flex-col" key={$?.name}>
                            <p className="text-black text-fs14">
                                {$?.name}
                            </p>
                            <p style={{ color: $.color }} className="text-black text-fs20 font-bold">
                                {$?.value}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    return null;
};

export default TargetInventoryGraph;
